// File: /pages/Transitions.jsx
import React, { useEffect, useState } from 'react';
import AccordionFolder from '/components/Accordions/AccordionFolder';
import ContentLibrarySidebar from '/components/Layout/ContentLibrarySidebar';
import ContentDisplayTabs from '/components/Tabs/ContentDisplayTabs';
import VideoLinksDisplay from '/components/ContentDisplay/VideoLinksDisplay';
import PerspectiveManagerDisplay from '/components/ContentDisplay/PerspectiveManagerDisplay';
import ClassificationDisplay from '/components/ContentDisplay/ClassificationDisplay';
import AlternativeNamesDisplay from '/components/ContentDisplay/AlternativeNamesDisplay';
import PerspectiveCarousel from '/components/ContentDisplay/PerspectiveCarousel';
import DatabaseImage from '/components/Media/DatabaseImage';
import Link from 'next/link';
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
import tabsStyles from '/components/Tabs/ContentDisplayTabs.module.css';
import displayStyles from '/components/ContentDisplay/ContentDisplay.module.css';
//import styles from './Transitions.module.css';

const TransitionContentDisplay = ({ transition }) => {
  const [activeTab, setActiveTab] = useState('Overview');

  // --- Overview Tab ---
  const overviewTabContent = (
    <div>
      <div className={tabsStyles.overviewContainer}>
        {/* Left Column: Metadata */}
        <div className={displayStyles.leftColumn}>
          <p>
            <strong>Status:</strong> {transition.status || 'N/A'}
          </p>
          <p>
            <strong>Submission Date:</strong>{' '}
            {transition.submission_date
              ? new Date(transition.submission_date).toLocaleDateString()
              : 'N/A'}
          </p>
          {transition.approval_date && (
            <p>
              <strong>Approval Date:</strong>{' '}
              {new Date(transition.approval_date).toLocaleDateString()}
            </p>
          )}
          <p>
            <strong>Created At:</strong>{' '}
            {transition.created_at
              ? new Date(transition.created_at).toLocaleDateString()
              : 'N/A'}
          </p>
          <p>
            <strong>Updated At:</strong>{' '}
            {transition.updated_at
              ? new Date(transition.updated_at).toLocaleDateString()
              : 'N/A'}
          </p>
        </div>
        {/* Right Column: Carousel and DMCA Link */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={transition} />
          <div className={displayStyles.dmcaLinkContainer}>
            <Link
              href={`/DMCA?contentHash=${transition.content_id}`}
              className={displayStyles.dmcaLink}
            >
              DMCA Request
            </Link>
          </div>
        </div>
      </div>
      <p className={displayStyles.description}>
        <strong>Description:</strong> {transition.description || 'No description available'}
      </p>
      <AlternativeNamesDisplay contentId={transition.content_id} />
    </div>
  );

  // --- Classification Tab ---
  const classificationTabContent = (
    <ClassificationDisplay content={transition} />
  );

  // --- Media Tab ---
  const galleryImages = Array.isArray(transition.gallery_images)
    ? transition.gallery_images
    : [];
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {galleryImages.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {galleryImages.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={transition} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={transition} />
    </div>
  );

  return (
    <ContentDisplayTabs
      tabs={[
        { label: 'Overview', content: overviewTabContent },
        { label: 'Media', content: mediaTabContent },
        { label: 'Classification', content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

const TransitionsPage = () => {
  const [transitions, setTransitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentCategoryMap, setParentCategoryMap] = useState({});

  // Fetch parent category mapping
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const res = await fetch('/api/categories/categories');
        if (!res.ok) throw new Error('Failed to fetch parent categories');
        const data = await res.json();
        if (data.success && data.categories) {
          const map = {};
          data.categories.forEach((cat) => {
            map[String(cat.id)] = cat.category_name;
          });
          setParentCategoryMap(map);
        }
      } catch (err) {
        console.error('Error fetching parent categories mapping:', err);
      }
    };
    fetchParentCategories();
  }, []);

  // Fetch all content items and filter for type "Transition"
  useEffect(() => {
    const fetchTransitions = async () => {
      try {
        const res = await fetch('/api/content/content');
        if (!res.ok) throw new Error('Failed to fetch transitions');
        const data = await res.json();
        let items = Array.isArray(data.content) ? data.content : [];
        items = items.filter(
          (item) =>
            item.type && item.type.trim().toLowerCase() === 'transition'
        );
        setTransitions(items);
      } catch (err) {
        console.error('Error fetching transitions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTransitions();
  }, []);

  if (loading) return <p>Loading transitions...</p>;
  if (error) return <p>Error: {error}</p>;

  // Group transitions by parent_category_id.
  const groups = {};
  transitions.forEach((transition) => {
    let parentCategories = [];
    if (transition.parent_category_id) {
      try {
        parentCategories = JSON.parse(transition.parent_category_id);
        if (!Array.isArray(parentCategories)) {
          parentCategories = [parentCategories];
        }
      } catch (err) {
        parentCategories = transition.parent_category_id.split(',').map((x) => x.trim());
      }
    }
    if (parentCategories.length === 0) {
      if (!groups['Uncategorized']) groups['Uncategorized'] = [];
      groups['Uncategorized'].push(transition);
    } else {
      parentCategories.forEach((cat) => {
        const key = String(cat.value || cat);
        if (!groups[key]) groups[key] = [];
        groups[key].push(transition);
      });
    }
  });

  const groupsArray = Object.keys(groups).map((groupKey) => {
    let groupName = 'Category ' + groupKey;
    if (groupKey === 'Uncategorized') {
      groupName = 'Uncategorized';
    } else if (parentCategoryMap[groupKey]) {
      groupName = parentCategoryMap[groupKey];
    } else if (parentCategoryMap[parseInt(groupKey)]) {
      groupName = parentCategoryMap[parseInt(groupKey)];
    }
    return {
      groupHeader: groupName,
      items: groups[groupKey].map((transition) => ({
        header: transition.name,
        content: <TransitionContentDisplay transition={transition} />,
      })),
    };
  });

  return (
    <section id="transitions" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      <div className={infoPaneStyles.infoPane}>
        <h1>Shuffle Dance Transitions - Connecting the Moves:</h1>
        <h2>
          Explore various transitions that connect shuffle dance moves seamlessly. Click on an accordion header to view the transition details.
        </h2>
      </div>
      <div className={sectionStyles.pageContainer}>
        <div className={sectionStyles.mainContent}>
          {groupsArray.length > 0 ? (
            <AccordionFolder groups={groupsArray} />
          ) : (
            <p>No transitions found.</p>
          )}
        </div>
        <ContentLibrarySidebar contentType="Transition" />
      </div>
    </section>
  );
};

export default TransitionsPage;
